import { Component, OnDestroy, OnInit } from '@angular/core';
import {
    FEATURE_OUTSTANDING_TASKS_VIEW,
    PersonsOrganisationDto,
    hasFeatureFlag,
    PreferencesService
} from '@flyfreely-portal-ui/flyfreely';
import {
    ColumnSortPreferences,
    TableColumn,
    TableConfig
} from '@flyfreely-portal-ui/flyfreely-table';
import {
    FormatDatePipe,
    FormatMaintenanceEquipmentCategoryPipe,
    FormatMaintenanceLogTypePipe,
    FormatMaintenanceStatusPipe
} from '@flyfreely-portal-ui/ui';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import {
    collapseOnLeaveAnimation,
    expandOnEnterAnimation
} from 'angular-animations';
import { MaintenanceDialogues } from 'libs/maintenance/src/lib/maintenance-dialogues.service';
import { EMPTY, Subject } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import {
    OutstandingTasksViewTableSetupUserPreferences,
    WidgetRegistrationWithSection
} from '../../../outstanding-tasks-view.service';
import {
    MaintenanceTaskWidgetDataService,
    UpcomingMaintenanceWidgetLog
} from '../maintenance-data.service';
import { applyPreferences } from 'libs/flyfreely-table/src/lib/helpers';

const UPCOMING_MAINTENANCE_IDENTIFIER = 'upcomingMaintenanceTaskWidget';

@Component({
    selector: 'upcoming-maintenance-task-widget',
    templateUrl: './upcoming-maintenance-widget.component.html',
    styleUrls: ['../../widget-styles.scss'],
    animations: [expandOnEnterAnimation(), collapseOnLeaveAnimation()]
})
export class UpcomingMaintenanceWidgetComponent implements OnInit, OnDestroy {
    organisation: PersonsOrganisationDto;
    showBody = true;

    // Replace below with the data to display in the table
    maintenanceLogs: UpcomingMaintenanceWidgetLog[];
    columnSorting: ColumnSortPreferences;
    userPreferences: OutstandingTasksViewTableSetupUserPreferences;

    //Static table variables
    availableColumns: TableColumn[];
    selectedColumns: string[];
    tableConfig: TableConfig;

    working = false;
    private ngUnsubscribe$ = new Subject<void>();
    constructor(
        private maintenanceDataService: MaintenanceTaskWidgetDataService,
        private maintenanceDialogues: MaintenanceDialogues,
        private workspaceStateService: WorkspaceStateService,
        formatMaintenanceEquipmentCategoryPipe: FormatMaintenanceEquipmentCategoryPipe,
        formatMaintenanceLogTypePipe: FormatMaintenanceLogTypePipe,
        formatMaintenanceStatusPipe: FormatMaintenanceStatusPipe,
        formatDatePipe: FormatDatePipe,
        private preferencesService: PreferencesService
    ) {
        this.availableColumns = [
            {
                value: 'resourceCategory',
                name: 'Resource Category',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                formatterFunction: c =>
                    formatMaintenanceEquipmentCategoryPipe.transform(c),
                linkFunction: row => this.viewMaintenance(row)
            },
            {
                value: 'name',
                name: 'Resource Name',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                linkFunction: row => this.viewMaintenance(row)
            },
            {
                value: 'serialNumber',
                name: 'Serial',
                searchable: false,
                selectable: false,
                defaultSelection: true
            },
            {
                value: 'maintenanceType',
                name: 'Type',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                formatterFunction: t =>
                    formatMaintenanceLogTypePipe.transform(t)
            },
            {
                value: 'status',
                name: 'Status',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                formatterFunction: s => formatMaintenanceStatusPipe.transform(s)
            },
            {
                value: 'requestTime',
                name: 'Request Time',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                formatterFunction: t => formatDatePipe.transform(t)
            }
        ];

        this.selectedColumns = null;

        this.maintenanceDataService.working$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                this.loadPreferences();
            });

        this.maintenanceDataService.upcomingMaintenanceLogs$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(maintenanceLogs => {
                this.maintenanceLogs = maintenanceLogs;
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    onToggleCollapse() {
        this.showBody = !this.showBody;
        this.userPreferences = {
            ...this.userPreferences,
            showBody: this.showBody
        };
        this.updateUserPreferences();
    }

    private loadPreferences() {
        if (!this.organisation) {
            return;
        }

        this.tableConfig = {
            timeZone: this.organisation.timeZone,
            limit: 3,
            actions: [],
            limitSelection: [3, 10, 25, 50, 100]
        };

        this.preferencesService
            .findPreferences<OutstandingTasksViewTableSetupUserPreferences>(
                'upcomingMaintenanceWidgetTable',
                this.organisation.id
            )
            .pipe(
                catchError(() => {
                    this.handleDefaultPreferences();
                    return EMPTY;
                })
            )
            .subscribe(preferences => {
                if (preferences === null) {
                    this.handleDefaultPreferences();
                } else {
                    this.applyPreferences(preferences);
                }
            });
    }

    private handleDefaultPreferences() {
        this.tableConfig.limit = 3; // reset limit to default
        this.applyPreferences({});
    }

    private applyPreferences(
        preferences: OutstandingTasksViewTableSetupUserPreferences
    ) {
        this.userPreferences = applyPreferences(
            preferences,
            this.availableColumns,
            this.tableConfig
        );
        this.selectedColumns = this.userPreferences.selectedColumns;
        this.columnSorting = this.userPreferences.columnSorting;
        this.tableConfig.limit = this.userPreferences.itemLimit;
        this.showBody = this.userPreferences.showBody ?? true;
    }

    updateSelectedColumns(selectedColumns: string[]) {
        this.selectedColumns = selectedColumns;
        if (
            this.columnSorting != null &&
            !this.selectedColumns.includes(this.columnSorting.column)
        ) {
            this.columnSorting = null;
        }
        this.userPreferences = {
            ...this.userPreferences,
            selectedColumns: selectedColumns,
            columnSorting: this.columnSorting || null
        };
        this.updateUserPreferences();
    }

    updateColumnSorting(sorting: ColumnSortPreferences) {
        this.columnSorting = sorting;
        this.userPreferences = {
            ...this.userPreferences,
            columnSorting: sorting
        };
        this.updateUserPreferences();
    }

    updateItemLimit(limit: number) {
        this.tableConfig.limit = limit;
        this.userPreferences = {
            ...this.userPreferences,
            itemLimit: limit
        };
        this.updateUserPreferences();
    }

    updateUserPreferences() {
        this.preferencesService
            .updatePreferences(
                'upcomingMaintenanceWidgetTable',
                this.organisation.id,
                this.userPreferences
            )
            .subscribe();
    }

    viewMaintenance(log: UpcomingMaintenanceWidgetLog) {
        this.maintenanceDialogues.showMaintenanceDetailsModal(
            this.organisation.id,
            log.maintenanceLog.id
        );
    }
}

export const upcomingMaintenanceTaskWidgetRegistration: WidgetRegistrationWithSection =
    {
        widgetIdentifier: UPCOMING_MAINTENANCE_IDENTIFIER,
        section: 'resources',
        component: UpcomingMaintenanceWidgetComponent,
        isAvailable: organisation =>
            hasFeatureFlag(organisation, FEATURE_OUTSTANDING_TASKS_VIEW)
    };
